import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Upload, X, Loader2, Link as LinkIcon } from 'lucide-react';
import { resolveImageUrl } from '../context/SettingsContext';
import VideoUploadDialog from './VideoUploadDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Erkennt Video-Dateien anhand der Endung. Für Hero-Slider & Kategorien-Kacheln
// erlauben wir zusätzlich zu Bildern kurze Autoplay-Clips (MP4/WebM/MOV).
export const isVideoUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  const clean = url.split('?')[0].toLowerCase();
  return clean.endsWith('.mp4') || clean.endsWith('.webm') || clean.endsWith('.mov');
};

const ImageUpload = ({ value, onChange, label, testId, height = 'h-40', allowVideo = false }) => {
  const [uploading, setUploading] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlValue, setUrlValue] = useState('');
  const [videoDraft, setVideoDraft] = useState(null); // File awaiting trim/crop
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isImg = file.type.startsWith('image/');
    const isVid = allowVideo && file.type.startsWith('video/');
    if (!isImg && !isVid) {
      toast.error(allowVideo ? 'Bitte wähle ein Bild oder Video (MP4/WebM)' : 'Bitte wähle eine Bilddatei');
      return;
    }

    const maxBytes = isVid ? 100 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxBytes) {
      toast.error(`Datei zu groß (max. ${Math.round(maxBytes / 1024 / 1024)}MB)`);
      return;
    }

    // Video → Editor-Dialog öffnen (Trim / Crop / Poster / Preset)
    if (isVid) {
      setVideoDraft(file);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const token = localStorage.getItem('adminToken');
    if (!token) {
      toast.error('Bitte melden Sie sich an');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      onChange(res.data.url);
      toast.success('Bild erfolgreich hochgeladen');
    } catch (error) {
      toast.error('Upload fehlgeschlagen: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleUrlSubmit = () => {
    if (urlValue.trim()) {
      onChange(urlValue.trim());
      setUrlValue('');
      setShowUrlInput(false);
      toast.success(isVideoUrl(urlValue.trim()) ? 'Video-URL gesetzt' : 'Bild-URL gesetzt');
    }
  };

  const isVid = isVideoUrl(value);
  const uploadHint = allowVideo ? 'Bild oder Video (max. 100 MB, wird optimiert)' : null;

  return (
    <div className="space-y-3" data-testid={testId}>
      {label && <label className="text-sm font-medium text-neutral-700">{label}</label>}

      {value ? (
        <div className="relative group">
          {isVid ? (
            <video
              src={resolveImageUrl(value)}
              className={`w-full ${height} object-cover rounded-2xl border border-neutral-200 bg-neutral-100`}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              data-testid={testId ? `${testId}-preview-video` : undefined}
            />
          ) : (
            <img
              src={resolveImageUrl(value)}
              alt="Vorschau"
              className={`w-full ${height} object-cover rounded-2xl border border-neutral-200`}
            />
          )}
          {isVid && (
            <span className="absolute top-2 left-2 bg-black/60 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">
              Video
            </span>
          )}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl flex items-center justify-center space-x-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="bg-white"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              <Upload className="h-4 w-4 mr-1" />
              Ändern
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="bg-white text-red-600 border-red-200"
              onClick={() => onChange('')}
            >
              <X className="h-4 w-4 mr-1" />
              Entfernen
            </Button>
          </div>
        </div>
      ) : (
        <div className={`w-full ${height} border-2 border-dashed border-neutral-300 rounded-2xl flex flex-col items-center justify-center space-y-3 bg-neutral-50`}>
          {uploading ? (
            <>
              <Loader2 className="h-8 w-8 text-sky-500 animate-spin" />
              <p className="text-sm text-neutral-600">Wird hochgeladen...</p>
            </>
          ) : (
            <>
              <Upload className="h-8 w-8 text-neutral-400" />
              <div className="flex flex-wrap gap-2 justify-center">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="rounded-full"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-4 w-4 mr-1" />
                  Hochladen
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="rounded-full"
                  onClick={() => setShowUrlInput(!showUrlInput)}
                >
                  <LinkIcon className="h-4 w-4 mr-1" />
                  URL einfügen
                </Button>
              </div>
              {uploadHint && <p className="text-[10px] text-neutral-400">{uploadHint}</p>}
            </>
          )}
        </div>
      )}

      {showUrlInput && (
        <div className="flex space-x-2">
          <Input
            type="url"
            placeholder={allowVideo ? 'https://…/media.mp4 oder .jpg' : 'https://example.com/bild.jpg'}
            value={urlValue}
            onChange={(e) => setUrlValue(e.target.value)}
            className="rounded-full"
          />
          <Button type="button" size="sm" onClick={handleUrlSubmit} className="rounded-full">
            OK
          </Button>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={allowVideo ? 'image/*,video/mp4,video/webm,video/quicktime' : 'image/*'}
        onChange={handleFileSelect}
        className="hidden"
      />

      {videoDraft && (
        <VideoUploadDialog
          open={!!videoDraft}
          file={videoDraft}
          onClose={() => setVideoDraft(null)}
          onUploaded={(data) => {
            onChange(data.url);
            setVideoDraft(null);
          }}
        />
      )}
    </div>
  );
};

export default ImageUpload;
